const BASE_URL = '/api';

function getToken(): string | null {
  return localStorage.getItem('falcon_token');
}

/**
 * Dispatch a custom event so the app can react to 401s globally
 * (e.g. redirect to /login without importing React Router here).
 */
function handleUnauthorized(): void {
  localStorage.removeItem('falcon_token');
  localStorage.removeItem('falcon_user');
  window.dispatchEvent(new Event('falcon:unauthorized'));
}

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const token = getToken();
  const response = await fetch(`${BASE_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options?.headers,
    },
  });

  if (response.status === 401) {
    handleUnauthorized();
    throw new Error('Session expired. Please log in again.');
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }

  return response.json();
}

export interface Stats {
  total: number;
  completed: number;
  failed: number;
  processing: number;
  todayCount: number;
  recentRuns: SchedulerRun[];
}

export interface Extraction {
  id: string;
  messageId: string;
  subject: string;
  sender: string;
  senderEmail: string;
  recipients: string[];
  ccRecipients: string[];
  receivedAt: string;
  extractedAt: string;
  bodyText?: string;
  bodyHtml?: string;
  hasAttachments: boolean;
  attachmentCount: number;
  blobUrl?: string;
  blobName?: string;
  fileType?: string;
  status: 'PENDING' | 'PROCESSING' | 'RECEIVED' | 'TASK_CREATED' | 'COMPLETED' | 'FAILED';
  isOutgoing: boolean;
  error?: string;
  mailboxEmail: string;
  folderName?: string;
  importance?: string;
  categories: string[];
  isRead: boolean;
  conversationId?: string;
  tickets?: number[];
  attachments: Attachment[];
}

export interface Attachment {
  id: string;
  name: string;
  contentType: string;
  size: number;
  isInline: boolean;
  contentId?: string;
  blobUrl?: string;
}

export interface SchedulerRun {
  id: string;
  startedAt: string;
  completedAt?: string;
  status: string;
  emailsFound: number;
  emailsProcessed: number;
  emailsSkipped: number;
  error?: string;
  durationMs?: number;
}

export interface PaginatedResult<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface SchedulerStatus {
  enabled: boolean;
  cron: string;
  isRunning: boolean;
  lastRunAt?: string;
  nextRunAt?: string;
}

export interface KeyValue {
  key: string;
  value: string;
}

export interface AiAnalysis {
  id: string;
  extractionId: string;
  isInsuranceRelated: boolean;
  documentType: string;
  summary: string;
  urgencyLevel: 'low' | 'medium' | 'high' | 'critical';
  flagged: boolean;
  flags: string[];
  actionItems: string[];
  modelUsed: string;
  inputTokens?: number;
  outputTokens?: number;
  processingMs?: number;
  error?: string;
  createdAt: string;
  extractedFields: {
    policyNumber?: string | null;
    insuredName?: string | null;
    insuredAddress?: string | null;
    effectiveDate?: string | null;
    expirationDate?: string | null;
    carrier?: string | null;
    broker?: { name?: string | null; agency?: string | null; phone?: string | null; email?: string | null } | null;
    coverageTypes?: string[];
    coverageLimits?: KeyValue[];
    deductibles?: KeyValue[];
    endorsements?: string[];
    premium?: { amount?: number | null; currency?: string | null; frequency?: string | null } | null;
    riskDescription?: string | null;
  };
}

export type FlagDataType = 'boolean' | 'string' | 'number' | 'json';

export interface FeatureFlag {
  id:          string;
  key:         string;
  value:       string;
  dataType:    FlagDataType;
  description: string | null;
  enabled:     boolean;
  createdAt:   string;
  updatedAt:   string;
}

// Generic HTTP helpers (for pages that do ad-hoc calls)
export const apiGet    = <T>(path: string)               => request<T>(path.replace(/^\/api/, ''));
export const apiPost   = <T>(path: string, body: unknown) => request<T>(path.replace(/^\/api/, ''), { method: 'POST',   body: JSON.stringify(body) });
export const apiPut    = <T>(path: string, body: unknown) => request<T>(path.replace(/^\/api/, ''), { method: 'PUT',    body: JSON.stringify(body) });
export const apiPatch  = <T>(path: string, body: unknown) => request<T>(path.replace(/^\/api/, ''), { method: 'PATCH',  body: JSON.stringify(body) });
export const apiDelete = <T>(path: string)               => request<T>(path.replace(/^\/api/, ''), { method: 'DELETE' });

export const api = {
  getStats: () => request<Stats>('/stats'),

  getSchedulerStatus: () => request<SchedulerStatus>('/scheduler/status'),

  triggerRun: () =>
    request<{ message: string }>('/scheduler/run', { method: 'POST' }),

  getSchedulerRuns: (page = 1) =>
    request<PaginatedResult<SchedulerRun>>(`/scheduler/runs?page=${page}`),

  getExtractions: (params: Record<string, string | number | undefined> = {}) => {
    const query = Object.entries(params)
      .filter(([, v]) => v !== undefined && v !== '')
      .map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`)
      .join('&');
    return request<PaginatedResult<Extraction>>(`/extractions?${query}`);
  },

  getExtraction: (id: string) => request<Extraction>(`/extractions/${id}`),

  getAiAnalysis: (id: string) => request<AiAnalysis>(`/extractions/${id}/analysis`),

  reanalyze: (id: string) =>
    request<{ message: string }>(`/extractions/${id}/analysis/reanalyze`, { method: 'POST' }),

  createTask: (id: string) =>
    request<{ message: string }>(`/extractions/${id}/create-task`, { method: 'POST' }),

  downloadEml: async (id: string, subject?: string): Promise<void> => {
    const token = getToken();
    const response = await fetch(`${BASE_URL}/extractions/${id}/download`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (response.status === 401) {
      handleUnauthorized();
      throw new Error('Session expired. Please log in again.');
    }
    if (!response.ok) {
      throw new Error(`Download failed: HTTP ${response.status}`);
    }
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = subject ? `${subject.replace(/[^\w .-]/g, '_').substring(0, 60)}.eml` : 'email.eml';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },

  getAmsActivities: (ticketId: number) => request<any>(`/ams/activities/${ticketId}`),

  downloadAttachment: async (extractionId: string, attachmentId: string, filename: string): Promise<void> => {
    const token = getToken();
    const response = await fetch(`${BASE_URL}/extractions/${extractionId}/attachments/${attachmentId}/download`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (response.status === 401) {
      handleUnauthorized();
      throw new Error('Session expired. Please log in again.');
    }
    if (!response.ok) {
      throw new Error(`Download failed: HTTP ${response.status}`);
    }
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },

  markAsRead: (id: string) =>
    request<{ id: string; isRead: boolean }>(`/extractions/${id}/read`, { method: 'PATCH' }),

  markAsUnread: (id: string) =>
    request<{ id: string; isRead: boolean }>(`/extractions/${id}/unread`, { method: 'PATCH' }),

  // ─── Email Actions ────────────────────────────────────────────────────

  sendEmail: (payload: {
    userEmail: string;
    subject: string;
    body: string;
    toRecipients: Array<{ emailAddress: string; name?: string }>;
    ccRecipients?: Array<{ emailAddress: string; name?: string }>;
    bccRecipients?: Array<{ emailAddress: string; name?: string }>;
  }) =>
    request<{ message: string }>('/emails/send', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  replyToEmail: (extractionId: string, payload: { body: string }) =>
    request<{ message: string }>(`/emails/${extractionId}/reply`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  replyAllToEmail: (extractionId: string, payload: { body: string }) =>
    request<{ message: string }>(`/emails/${extractionId}/reply-all`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  forwardEmail: (
    extractionId: string,
    payload: {
      toRecipients: Array<{ emailAddress: string; name?: string }>;
      body?: string;
    }
  ) =>
    request<{ message: string }>(`/emails/${extractionId}/forward`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  getSentItems: (params: {
    page?: number;
    pageSize?: number;
    search?: string;
    mailboxEmail?: string;
    dateFrom?: string;
    dateTo?: string;
  } = {}) => {
    const query = Object.entries(params)
      .filter(([, v]) => v !== undefined && v !== '')
      .map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`)
      .join('&');
    return request<PaginatedResult<Extraction>>(`/sent-emails${query ? `?${query}` : ''}`);
  },
};
